import * as Sentry from '@sentry/react';
import { browserTracingIntegration, replayIntegration } from '@sentry/react';

/**
 * Initialize Sentry error tracking and performance monitoring
 * Should be called before React renders
 */
export const initializeSentry = () => {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  const environment = import.meta.env.VITE_SENTRY_ENVIRONMENT || 'development';
  const appVersion = import.meta.env.VITE_APP_VERSION || '1.0.0';

  if (environment === 'development' || !dsn) return; // Disable Sentry in development for now

  Sentry.init({
    dsn,
    environment,
    release: appVersion,
    integrations: [
      browserTracingIntegration({
        // Sample rate for performance monitoring
        // 0.1 = 10% of transactions
        // Set to 1.0 in development, lower in production
        tracingOrigins: [
          'localhost',
          /^\//,
          // API endpoints
        ],
      }),
      replayIntegration({
        // Capture 100% of replays in development, 20% in production
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    // Performance Monitoring
    tracesSampleRate: 0.5,
    // Session Replay
    replaysSessionSampleRate: 0.1, // 10% of sessions
    replaysOnErrorSampleRate: 1.0, // 100% of sessions with errors
    // Capture unhandled promise rejections
    attachStacktrace: true,
    // Ignore certain errors
    denyUrls: [
      // Browser extensions
      /extensions\//i,
      /^chrome:\/\//i,
    ],
    // Set the max breadcrumbs to capture
    maxBreadcrumbs: 100,
    // Configure beforeSend to filter out errors if needed
    beforeSend(event, hint) {
      // Filter out 404 errors if desired
      if (event.exception) {
        const error = hint.originalException;
        if (error?.response?.status === 404) {
          return null; // Don't send 404 errors
        }
      }
      return event;
    },
  });
};

/**
 * Set user context for error tracking
 * Call this when user logs in
 */
export const setSentryUser = (userId, email = null, username = null) => {
  if (!import.meta.env.VITE_SENTRY_DSN) return;

  Sentry.setUser({
    id: userId,
    email: email || undefined,
    username: username || undefined,
  });
};

/**
 * Clear user context on logout
 */
export const clearSentryUser = () => {
  if (!import.meta.env.VITE_SENTRY_DSN) return;
  Sentry.setUser(null);
};

/**
 * Add custom breadcrumb for debugging
 */
export const addSentryBreadcrumb = (message, data = {}, level = 'info') => {
  if (!import.meta.env.VITE_SENTRY_DSN) return;

  Sentry.captureMessage(message, level);
  Sentry.addBreadcrumb({
    message,
    level,
    data,
    timestamp: Date.now() / 1000,
  });
};

/**
 * Capture an exception to Sentry
 */
export const captureException = (error, context = {}) => {
  if (!import.meta.env.VITE_SENTRY_DSN) return;

  Sentry.withScope((scope) => {
    Object.entries(context).forEach(([key, value]) => {
      scope.setContext(key, value);
    });
    Sentry.captureException(error);
  });
};

/**
 * Capture a message to Sentry
 */
export const captureMessage = (message, level = 'info') => {
  if (!import.meta.env.VITE_SENTRY_DSN) return;
  Sentry.captureMessage(message, level);
};

export default Sentry;
